/**
 * Passwords in the vault (docs/ARCHITECTURE.md §7, "Passwords in the vault"):
 * the list under the notebooks, an item, and the switch in the Vault dialog
 * that shows them (`Passwords`, `PasswordItem`, `store/vault.ts`).
 */
export const passwords = {
	/** What the list is called wherever the app names it: the sidebar, the bar, the tab's title, search. */
	label: 'Passwords',
	/** The switch in the Vault dialog's step for this device. */
	toggle: {
		label: 'Passwords',
		text: 'Show Passwords under the notebooks on this device.',
	},
	list: {
		/** The `+` in the list's header. */
		add: 'New password',
		/** The field over the list that narrows it, its label and placeholder. */
		filter: 'Filter passwords',
		empty: 'Passwords you add here are encrypted with this source’s vault, as secrets in notes are.',
		/** Nothing in the list has a label that holds what is typed in the filter. */
		noMatch: 'No password’s label has “{query}” in it.',
	},
	/** The item's column, with nothing chosen. */
	none: 'Choose a password, or add one with +.',
	item: {
		/** The item's own heading, while a new one has no label yet. */
		new: 'New password',
		label: 'Label',
		/** Under the label's field: what it is for, and that it is plain text. */
		labelHint: 'The app, site or service. Not encrypted.',
		/** The web address the password fills in on, from the bookmarklet (docs/ARCHITECTURE.md §7, "Filling in on a site"). */
		site: 'Site',
		/** Under the site's field. */
		siteHint: 'The web address it fills in on, and the sites under it. Encrypted.',
		/** Under the site's field, in place of the hint, where what is in it is not a web address. */
		notASite: 'Not a web address, so it fills in nowhere.',
		/** Under an empty site's field, where the label is a web address: `{address}` is its host. */
		useAsSite: 'Use {address} as the site',
		username: 'Username',
		password: 'Password',
		notes: 'Notes',
		/** Where a field has nothing in it. */
		blank: 'None',
		/** Read out for the dots a password hidden is shown as. */
		hidden: 'Hidden',
		show: 'Show password',
		hide: 'Hide password',
		copyUsername: 'Copy username',
		copyPassword: 'Copy password',
		/** A password an Update from the fill window replaced, kept with the item. */
		previous: 'Previous password',
		/** The same, with the day it was replaced: `{date}` is that day. */
		previousUntil: 'Previous password, until {date}',
		showPrevious: 'Show previous password',
		hidePrevious: 'Hide previous password',
		copyPrevious: 'Copy previous password',
		copied: 'Copied.',
		notCopied: 'That could not be put on the clipboard.',
		/** The label's address, as a screen reader names its button: `{address}` is where it goes, shortened. */
		openAddress: 'Open {address}',
		edit: 'Edit',
		delete: 'Delete',
		done: 'Done',
		cancel: 'Cancel',
		/** Read out over the fields of an item open to be edited. */
		editing: 'Edit {label}',
		locked: 'Locked. Unlock the vault to see this password.',
		unlock: 'Unlock',
		/** Said while the item is opened, the device's own lock perhaps being asked. */
		opening: 'Opening…',
		differentKey:
			'This password was made with a different key, which this device does not have.',
		addKey: 'Add its key',
		unreadable: 'This password cannot be read by this version of the app.',
		/** Said in the form, where what was typed could not be encrypted into the item. */
		notSealed:
			'What you typed could not be encrypted, and is not saved yet. Keep typing to try again.',
		/** Said in a new item's form, where the device could not keep it: it stays open until it can. */
		notStored:
			'This password is not saved yet. Press Done to try again, or copy what you typed somewhere safe.',
		notDeleted: 'That password could not be deleted.',
		/** A change to an item that could not be written on this device. */
		notSaved:
			'Changes to this password are not being saved on this device. Copy what you typed somewhere safe, then reload.',
	},
	/** Generate, under the password being edited, and how it makes one. */
	generate: {
		/** The generator's controls, together. */
		group: 'Make a new password',
		make: 'Generate',
		length: 'Length',
		symbols: 'Symbols',
		/** Said once Generate has filled the password in. */
		made: 'A new password was made, and is shown above.',
	},
} as const;
